import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import WorkCard from '@/components/base/WorkCard';
import SectionHeading from '@/components/base/SectionHeading';
import { useSeo } from '@/hooks/useSeo';
import { actor } from '@/mocks/actor';
import { works } from '@/mocks/works';

export default function About() {
  useSeo({
    title: '夏目彩春个人资料及代表作品',
    description:
      '夏目彩春个人资料页，介绍其出生年月、国籍、主要成就等基本信息，并精选其代表影视作品，全面了解这位实力派女演员的职业背景与戏路。',
    keywords: '夏目彩春个人资料,夏目彩春简介,演员介绍,代表作品',
  });

  const representative = actor.representativeIds
    .map((id) => works.find((w) => w.id === id))
    .filter((w): w is (typeof works)[number] => Boolean(w));

  return (
    <div className="bg-background-50">
      {/* Hero */}
      <div className="relative overflow-hidden bg-primary-900">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20elegant%20dark%20textured%20background%20of%20soft%20blurred%20stage%20curtain%20and%20warm%20spotlight%20glow%2C%20deep%20navy%20and%20amber%20tones%2C%20minimal%20cinematic%20atmosphere%2C%20refined%20composition&width=1600&height=700&seq=natsume-about-bg&orientation=landscape"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative w-full max-w-content mx-auto px-4 md:px-8 py-8 md:py-12">
          <div className="[&_nav]:text-background-100/70 [&_span]:text-background-100/80 [&_a:hover]:text-accent-400">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '演员介绍' }]} />
          </div>
        </div>
      </div>

      <div className="w-full max-w-content mx-auto px-4 md:px-8 py-10 md:py-14">
        {/* Profile */}
        <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-8 md:gap-12 items-start">
          <div className="w-full max-w-[320px]">
            <div className="rounded-lg overflow-hidden border border-background-200/80 bg-background-200">
              <img
                src={actor.portrait}
                alt="夏目彩春肖像"
                title="夏目彩春 - 演员个人资料"
                className="w-full aspect-[4/5] object-top object-cover"
              />
            </div>
          </div>

          <div className="pt-1">
            <span className="font-label text-xs tracking-[0.25em] uppercase text-accent-600">Actor Profile</span>
            <h1 className="font-heading font-black text-4xl md:text-5xl text-foreground-950 mt-2">{actor.name}</h1>
            <p className="font-label text-sm text-foreground-500 mt-1.5 tracking-wide">{actor.nameEn}</p>
            <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">{actor.tagline}</p>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {actor.stats.map((stat) => (
                <div key={stat.label} className="rounded-lg bg-background-100 border border-background-200/80 p-4">
                  <div className="font-label text-2xl text-primary-600">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Info + Bio */}
        <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4">
            <h2 className="font-heading font-bold text-xl text-foreground-950 mb-5 flex items-center gap-2.5">
              <span className="w-1.5 h-5 rounded-full bg-accent-500" />
              基本信息
            </h2>
            <div className="bg-background-100 border border-background-200/80 rounded-lg divide-y divide-background-200/70">
              {actor.info.map((info) => (
                <div key={info.label} className="flex items-start justify-between gap-4 px-5 py-3.5">
                  <span className="text-sm text-foreground-500 shrink-0">{info.label}</span>
                  <span className="text-sm text-foreground-900 text-right">{info.value}</span>
                </div>
              ))}
            </div>
          </aside>

          <div className="lg:col-span-8">
            <h2 className="font-heading font-bold text-xl text-foreground-950 mb-5 flex items-center gap-2.5">
              <span className="w-1.5 h-5 rounded-full bg-accent-500" />
              职业生涯
            </h2>
            <div className="flex flex-col gap-4">
              {actor.bio.map((p, i) => (
                <p key={i} className="text-sm md:text-base text-foreground-700 leading-loose">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </div>

        {/* Representative works */}
        <div className="mt-14 md:mt-20">
          <SectionHeading
            eyebrow="Representative Works"
            title="代表作品精选"
            description="从青春校园到悬疑权谋，这些作品见证了夏目彩春戏路的拓展与表演的成熟。"
            moreTo="/category"
            moreLabel="全部作品"
          />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {representative.map((work) => (
              <WorkCard key={work.id} work={work} />
            ))}
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center text-center bg-background-100 border border-background-200/80 rounded-lg p-8 md:p-12">
          <h3 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
            想了解她的最新动态？
          </h3>
          <p className="mt-3 text-sm text-foreground-600 max-w-xl">
            浏览最新影视资讯与作品更新，第一时间掌握夏目彩春的演艺动态。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/news"
              className="px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              查看最新资讯
            </Link>
            <Link
              to="/category"
              className="px-6 py-3 rounded-md border border-background-300 text-foreground-700 text-sm hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              浏览影视作品
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}